import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, Upload, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ApiError, getApiErrorMessage } from "@/lib/api";
import { formatCnpj } from "@/lib/format";
import { formatDateTimeSP } from "@/lib/formatDate";
import {
  deactivateCertificate,
  listCertificates,
  listEstablishments,
  uploadCertificate,
  type Certificate,
  type Establishment,
} from "@/lib/adminCertificatesApi";

const SITUATION_LABEL: Record<Certificate["situation"], string> = {
  VALIDO: "Válido no período",
  A_VENCER: "A vencer",
  VENCIDO: "Vencido",
  NAO_VIGENTE: "Ainda não vigente",
};

/** Mensagem de alerta de vencimento (30/15/7 dias) — só a validade temporal, não confiança/revogação. */
function expiryAlert(cert: Certificate | undefined): string | null {
  if (!cert || !cert.inUse) return null;
  if (cert.situation === "VENCIDO") return "O certificado em uso está VENCIDO — substitua para voltar a operar.";
  if (cert.situation === "NAO_VIGENTE") return "O certificado em uso ainda não é vigente.";
  const d = cert.daysRemaining;
  if (d <= 7) return `O certificado em uso vence em ${d} dia(s). Substitua com urgência.`;
  if (d <= 15) return `O certificado em uso vence em ${d} dia(s).`;
  if (d <= 30) return `O certificado em uso vence em ${d} dia(s).`;
  return null;
}

/**
 * Certificado digital A1 por estabelecimento (B). Upload só por esta interface segura; o material privado nunca é
 * exibido nem baixável. Nenhuma ação aqui consulta a SEFAZ.
 */
export default function AdminCertificates() {
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [establishmentsError, setEstablishmentsError] = useState<string | null>(null);
  const [establishmentId, setEstablishmentId] = useState("");
  const [certs, setCerts] = useState<Certificate[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    listEstablishments()
      .then((list) => { setEstablishments(list); setEstablishmentsError(null); })
      .catch((e) => setEstablishmentsError(
        e instanceof ApiError && e.status === 403
          ? "Sem permissão para listar estabelecimentos (SUPRIMENTOS.ESTABELECIMENTO.VISUALIZAR)."
          : "Não foi possível carregar os estabelecimentos.",
      ));
  }, []);

  const loadCerts = async (id: string) => {
    if (!id) { setCerts([]); return; }
    setListBusy(true);
    try {
      setCerts(await listCertificates(id));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar certificados"));
    } finally {
      setListBusy(false);
    }
  };

  useEffect(() => { void loadCerts(establishmentId); }, [establishmentId]);

  const active = certs.find((c) => c.inUse);
  const alert = expiryAlert(active);

  const upload = async () => {
    if (!establishmentId) { toast.error("Selecione um estabelecimento."); return; }
    if (!file) { toast.error("Selecione o arquivo do certificado (.pfx/.p12)."); return; }
    if (!password) { toast.error("Informe a senha do certificado."); return; }
    setBusy(true);
    try {
      await uploadCertificate(establishmentId, file, password);
      toast.success("Certificado enviado.");
      setFile(null); setPassword("");
      if (fileRef.current) fileRef.current.value = "";
      await loadCerts(establishmentId);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao enviar o certificado"));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (cert: Certificate) => {
    setBusy(true);
    try {
      await deactivateCertificate(cert.id);
      toast.success("Certificado desativado.");
      await loadCerts(establishmentId);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao desativar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container max-w-5xl py-6 space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Certificado digital</h1>
        <p className="text-sm text-muted-foreground">
          Certificado A1 por estabelecimento, usado na comunicação fiscal. O arquivo e a senha são enviados só por
          esta tela; o material privado nunca é exibido nem baixável. Nenhuma ação aqui consulta a SEFAZ.
        </p>
      </header>

      <section className="rounded-xl border border-border p-4 space-y-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Estabelecimento</span>
          <select
            className="border rounded px-2 py-1 text-sm text-foreground disabled:opacity-60 max-w-md"
            value={establishmentId}
            onChange={(e) => setEstablishmentId(e.target.value)}
            disabled={!!establishmentsError}
            aria-label="Estabelecimento"
          >
            <option value="">selecione…</option>
            {establishments.map((est) => (
              <option key={est.id} value={est.id}>{est.name} — {formatCnpj(est.cnpj)}</option>
            ))}
          </select>
        </label>
        {establishmentsError && <p className="text-[11px] text-warning" role="note">{establishmentsError}</p>}
      </section>

      {alert && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm">
          <ShieldAlert className="h-4 w-4 text-warning" /> {alert}
        </div>
      )}

      {establishmentId && (
        <section className="rounded-xl border border-border p-4 space-y-3">
          <div className="text-sm font-semibold">Enviar / substituir certificado</div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Arquivo (.pfx/.p12)
              <input
                ref={fileRef}
                type="file"
                accept=".pfx,.p12,application/x-pkcs12"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="text-sm"
                aria-label="Arquivo do certificado"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Senha do certificado
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border rounded px-2 py-1 text-sm text-foreground"
                autoComplete="off"
                aria-label="Senha do certificado"
              />
            </label>
            <Button size="sm" onClick={() => void upload()} disabled={busy || !file || !password}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Enviar certificado
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            A senha abre o material no envio e não é armazenada. A substituição só desativa o atual se o novo for válido.
          </p>
        </section>
      )}

      {establishmentId && (
        <section className="rounded-xl border border-border p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            Certificados {listBusy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          {certs.length === 0 ? (
            <div className="text-xs text-muted-foreground">Nenhum certificado cadastrado para este estabelecimento.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="p-1">titular</th>
                    <th className="p-1">documento</th>
                    <th className="p-1">emissor</th>
                    <th className="p-1">validade</th>
                    <th className="p-1">dias</th>
                    <th className="p-1">situação</th>
                    <th className="p-1">em uso</th>
                    <th className="p-1">enviado em</th>
                    <th className="p-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {certs.map((c) => (
                    <tr key={c.id} className="border-t border-border">
                      <td className="p-1">{c.holderName}</td>
                      <td className="p-1">{formatCnpj(c.holderDocument)}</td>
                      <td className="p-1">{c.issuerName}</td>
                      <td className="p-1">{formatDateTimeSP(c.validFrom)} – {formatDateTimeSP(c.validTo)}</td>
                      <td className="p-1 tabular-nums">{c.daysRemaining}</td>
                      <td className="p-1">{SITUATION_LABEL[c.situation]}</td>
                      <td className="p-1">
                        {c.inUse
                          ? <span className="inline-flex items-center gap-1 text-success"><ShieldCheck className="h-3 w-3" /> em uso</span>
                          : <span className="text-muted-foreground">substituído</span>}
                      </td>
                      <td className="p-1">{formatDateTimeSP(c.uploadedAt)}</td>
                      <td className="p-1">
                        {c.inUse && (
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void deactivate(c)}>
                            <Ban className="h-3 w-3" /> Desativar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
